import { cn } from "@/utils";
import type { ReactNode } from "react";

/**
 * Quanto de um total já foi — etapas de onboarding, fila processada, upload.
 *
 * Existia copiado em oito telas (fila, treinamentos, CSAT, faixa de cobrança,
 * upload de planilha…), sempre como duas `div` com `width` em porcentagem.
 * Nenhuma delas tinha `role="progressbar"`: para quem escuta a página, a barra
 * simplesmente não existia.
 *
 * **Não é gráfico.** Uma barra que compara categorias entre si é outra coisa;
 * esta responde uma pergunta só — "quanto falta?".
 *
 * Com `kind="meter"` ela responde outra, vizinha: "quanto, numa escala
 * fechada?" — uma nota de 0 a 100, um critério de avaliação. O desenho é o
 * mesmo; muda só o que o leitor de tela anuncia (`role="meter"`), porque uma
 * nota não está "em andamento" e não vai terminar.
 */

export interface ProgressBarProps {
  /** Quanto já foi, na mesma unidade de `max`. */
  value: number;
  /** Total. `100` faz `value` ser lido direto como porcentagem. */
  max?: number;
  /**
   * O que está progredindo. Vira o nome acessível da barra e, salvo
   * `hideLabel`, o texto à esquerda.
   */
  label: string;
  hideLabel?: boolean;
  /** À direita do rótulo: "8/9", "62%", "120 de 300 enviadas". */
  hint?: ReactNode;
  /**
   * O que a barra anuncia a quem escuta a página, quando o número cru não
   * basta: "8 de 9 etapas concluídas".
   */
  valueText?: string;
  tone?: "brand" | "success" | "warning" | "danger";
  /** `sm` (4px) em linha de tabela, `md` (8px) no resto. */
  size?: "sm" | "md";
  /**
   * `progress` (padrão): quanto de um total já foi — etapas, fila, upload.
   * `meter`: um valor parado numa escala fechada — nota, critério de 0 a 100.
   * Só muda a semântica (`role="progressbar"` × `role="meter"`), não o desenho.
   */
  kind?: "progress" | "meter";
  className?: string;
}

const toneClass = {
  brand: "bg-ds-brand-500",
  success: "bg-ds-success-700",
  warning: "bg-ds-warning-700",
  danger: "bg-ds-danger-700",
} as const;

export const ProgressBar = ({
  value,
  max = 100,
  label,
  hideLabel,
  hint,
  valueText,
  tone = "brand",
  size = "md",
  kind = "progress",
  className,
}: ProgressBarProps) => {
  // Dado torto não pode virar barra torta: `max` zerado (uma lista vazia, uma
  // fila sem itens) daria divisão por zero e uma barra de largura NaN.
  const limite = max > 0 ? max : 1;
  const atual = Math.min(Math.max(value, 0), limite);
  const porcento = (atual / limite) * 100;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {hideLabel && !hint ? null : (
        <div className="flex items-baseline justify-between gap-3">
          {hideLabel ? null : (
            <span className="font-ds-sans text-ds-small text-ds-ink-900 font-medium">
              {label}
            </span>
          )}
          {hint ? (
            <span className="font-ds-sans text-ds-small text-ds-ink-600 ds-numeric">
              {hint}
            </span>
          ) : null}
        </div>
      )}

      <div
        role={kind === "meter" ? "meter" : "progressbar"}
        aria-label={label}
        aria-valuenow={atual}
        aria-valuemin={0}
        aria-valuemax={limite}
        aria-valuetext={valueText}
        className={cn(
          "bg-ds-ink-100 w-full overflow-hidden rounded-full",
          size === "sm" ? "h-1" : "h-2"
        )}
      >
        <div
          className={cn(
            "ease-ds-out h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none",
            toneClass[tone]
          )}
          style={{ width: `${porcento}%` }}
        />
      </div>
    </div>
  );
};
